import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import adminApi from '../utils/adminApi';
import { toast } from 'sonner';
import { runBackup, getLastBackupTime } from '../utils/autoBackup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataExport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exportType, setExportType] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [cachedInfo, setCachedInfo] = useState({});
  const [lastBackup, setLastBackup] = useState(getLastBackupTime());
  const [backingUp, setBackingUp] = useState(false);

  // Monitor online/offline status
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // Load cached info on mount
    const cached = {};
    ['available', 'picked_up', 'all', 'history'].forEach(type => {
      const saved = localStorage.getItem(`ashop_export_${type}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          cached[type] = { count: parsed.data.length, savedAt: parsed.savedAt };
        } catch (e) {}
      }
    });
    setCachedInfo(cached);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const saveToCache = (type, data) => {
    try {
      const cacheEntry = { data, savedAt: new Date().toISOString() };
      localStorage.setItem(`ashop_export_${type}`, JSON.stringify(cacheEntry));
      setCachedInfo(prev => ({ ...prev, [type]: { count: data.length, savedAt: cacheEntry.savedAt } }));
    } catch (e) {
      console.error('Cache save failed:', e);
    }
  };

  const loadFromCache = (type) => {
    try {
      const saved = localStorage.getItem(`ashop_export_${type}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        setExportData(parsed.data);
        setExportType(type);
        toast.success(`Loaded ${parsed.data.length} records from offline cache (saved ${new Date(parsed.savedAt).toLocaleString()})`);
        return true;
      }
    } catch (e) {}
    toast.error('No offline data available for this category');
    return false;
  };

  const fetchAndExport = async (type) => {
    // If offline, load from cache
    if (isOffline) {
      loadFromCache(type);
      return;
    }

    setLoading(true);
    setExportType(type);
    try {
      let packages = [];
      if (type === 'available') {
        const res = await axios.get(`${API}/packages?active_only=true`);
        packages = res.data.filter(p => !p.is_picked_up);
      } else if (type === 'picked_up') {
        const res = await axios.get(`${API}/packages?active_only=false`);
        packages = res.data.filter(p => p.is_picked_up);
      } else if (type === 'all') {
        const res = await axios.get(`${API}/packages?active_only=false`);
        packages = res.data;
      } else if (type === 'history') {
        const res = await axios.get(`${API}/packages/history/all`);
        packages = res.data;
      }
      setExportData(packages);
      // Auto-save to cache for offline access
      saveToCache(type, packages);
      toast.success(`Loaded ${packages.length} records (saved for offline use)`);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to cache on network error
      if (!loadFromCache(type)) {
        toast.error('Failed to load data and no offline cache available');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualBackup = async () => {
    setBackingUp(true);
    const success = await runBackup();
    if (success) {
      setLastBackup(getLastBackupTime());
      // Refresh cached info
      const cached = {};
      ['available', 'picked_up', 'all', 'history'].forEach(type => {
        const saved = localStorage.getItem(`ashop_export_${type}`);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            cached[type] = { count: parsed.data.length, savedAt: parsed.savedAt };
          } catch (e) {}
        }
      });
      setCachedInfo(cached);
      toast.success('Backup complete! All data saved for offline access.');
    } else {
      toast.error('Backup failed. Check your internet connection.');
    }
    setBackingUp(false);
  };

  const generateCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = ['Box #', 'Recipient Name', 'Carrier', 'Phone', 'Email', 'Address', 'Status', 'Scanned Date', 'Pickup Date'];
    const rows = data.map(pkg => [
      pkg.box_number || '',
      `"${(pkg.recipient_name || '').replace(/"/g, '""')}"`,
      `"${(pkg.carrier || '').replace(/"/g, '""')}"`,
      `"${(pkg.phone || '').replace(/"/g, '""')}"`,
      `"${(pkg.email || '').replace(/"/g, '""')}"`,
      `"${(pkg.address || '').replace(/"/g, '""')}"`,
      pkg.is_picked_up ? 'Picked Up' : (pkg.archived ? 'Archived' : 'Available'),
      pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : '',
      pkg.pickup_date ? new Date(pkg.pickup_date).toLocaleDateString() : ''
    ]);
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  const handleDownloadCSV = () => {
    if (!exportData || exportData.length === 0) {
      toast.error('No data to export');
      return;
    }
    const csv = generateCSV(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `ashop_${exportType}_${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded!');
  };

  const handlePrint = () => {
    if (!exportData || exportData.length === 0) {
      toast.error('No data to print');
      return;
    }
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const typeLabel = exportType === 'available' ? 'Available Packages' : exportType === 'picked_up' ? 'Picked Up Packages' : exportType === 'history' ? 'Archived History' : 'All Packages';
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>Ashop - ${typeLabel}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 30px; color: #333; }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 3px solid #4F46E5; }
        .header h1 { font-size: 22px; color: #4F46E5; }
        .header p { font-size: 12px; color: #666; }
        .meta { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 13px; color: #555; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #4F46E5; color: white; padding: 10px 8px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
        td { padding: 8px; border-bottom: 1px solid #E5E7EB; }
        tr:nth-child(even) { background: #F9FAFB; }
        .status-available { color: #059669; font-weight: 600; }
        .status-picked { color: #2563EB; font-weight: 600; }
        .status-archived { color: #9CA3AF; font-weight: 600; }
        .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #E5E7EB; text-align: center; font-size: 11px; color: #999; }
        .total { margin-top: 16px; text-align: right; font-size: 14px; font-weight: 600; color: #4F46E5; }
        @media print { body { padding: 15px; } }
      </style></head><body>
        <div class="header">
          <div><h1>Ashop Rotterdam</h1><p>Package Management Report</p></div>
          <div style="text-align:right"><p>${typeLabel}</p><p>${date}</p></div>
        </div>
        <div class="meta">
          <span>Total Records: <strong>${exportData.length}</strong></span>
        </div>
        <table>
          <thead><tr>
            <th>#</th><th>Box</th><th>Recipient</th><th>Carrier</th><th>Phone</th><th>Status</th><th>Scanned</th><th>Pickup Date</th>
          </tr></thead>
          <tbody>
            ${exportData.map((pkg, i) => {
              const status = pkg.is_picked_up ? 'Picked Up' : (pkg.archived ? 'Archived' : 'Available');
              const statusClass = pkg.is_picked_up ? 'status-picked' : (pkg.archived ? 'status-archived' : 'status-available');
              return `<tr>
                <td>${i + 1}</td>
                <td>${pkg.box_number || '-'}</td>
                <td>${pkg.recipient_name || '-'}</td>
                <td>${pkg.carrier || '-'}</td>
                <td>${pkg.phone || '-'}</td>
                <td class="${statusClass}">${status}</td>
                <td>${pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : '-'}</td>
                <td>${pkg.pickup_date ? new Date(pkg.pickup_date).toLocaleDateString() : '-'}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
        <div class="total">Total: ${exportData.length} packages</div>
        <div class="footer">Generated by Ashop Rotterdam Package Management System</div>
      </body></html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const exportOptions = [
    { type: 'available', label: 'Available Packages', icon: 'fas fa-boxes', color: 'from-green-500 to-emerald-500', desc: 'Currently in inventory, waiting for pickup' },
    { type: 'picked_up', label: 'Picked Up', icon: 'fas fa-check-circle', color: 'from-blue-500 to-indigo-500', desc: 'Packages that have been collected' },
    { type: 'all', label: 'All Active Packages', icon: 'fas fa-layer-group', color: 'from-purple-500 to-pink-500', desc: 'Both available and picked up packages' },
    { type: 'history', label: 'Archived History', icon: 'fas fa-archive', color: 'from-gray-500 to-gray-600', desc: 'Packages moved to history (7+ days after pickup)' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center space-x-2 px-5 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200"
            data-testid="back-to-admin-btn"
          >
            <i className="fas fa-arrow-left"></i>
            <span>Back to Admin</span>
          </button>
        </div>

        {/* Offline Banner */}
        {isOffline && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-4 mb-6 flex items-center space-x-3" data-testid="offline-banner">
            <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="fas fa-wifi-slash text-orange-600"></i>
            </div>
            <div>
              <p className="font-bold text-orange-800">You're Offline</p>
              <p className="text-orange-600 text-sm">Showing cached data. Connect to the internet to get the latest records.</p>
            </div>
          </div>
        )}

        {/* Title + Backup Status */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <i className="fas fa-file-csv text-indigo-600 text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Data Export</h1>
                <p className="text-gray-500 text-sm">Download or print your package data — works offline too</p>
              </div>
            </div>
            <button
              onClick={handleManualBackup}
              disabled={backingUp || isOffline}
              className={`inline-flex items-center space-x-2 px-5 py-3 font-semibold rounded-xl transition-all shadow-md ${
                backingUp || isOffline
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
              }`}
              data-testid="backup-now-btn"
            >
              <i className={`fas ${backingUp ? 'fa-spinner fa-spin' : 'fa-cloud-download-alt'}`}></i>
              <span>{backingUp ? 'Backing up...' : 'Backup Now'}</span>
            </button>
          </div>
          
          {/* Auto-backup info */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <i className="fas fa-clock text-green-500"></i>
              <span className="text-sm text-gray-500">
                Auto-backup: <strong className="text-green-600">Every 12 hours</strong>
              </span>
            </div>
            <div className="text-sm text-gray-400">
              {lastBackup ? (
                <span>Last backup: <strong className="text-gray-600">{new Date(lastBackup).toLocaleString()}</strong></span>
              ) : (
                <span>No backup yet — click "Backup Now"</span>
              )}
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {exportOptions.map((opt) => (
            <button
              key={opt.type}
              onClick={() => fetchAndExport(opt.type)}
              disabled={loading}
              className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all duration-300 text-left hover:shadow-xl hover:scale-105 ${
                exportType === opt.type ? 'border-indigo-500' : 'border-transparent'
              }`}
              data-testid={`export-${opt.type}-btn`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 bg-gradient-to-r ${opt.color} rounded-xl flex items-center justify-center`}>
                  <i className={`${opt.icon} text-white text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">{opt.label}</h3>
                  <p className="text-gray-400 text-sm">{opt.desc}</p>
                </div>
              </div>
              {exportType === opt.type && exportData && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <span className="text-indigo-600 font-semibold text-sm">{exportData.length} records loaded</span>
                </div>
              )}
              {cachedInfo[opt.type] && !(exportType === opt.type && exportData) && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center space-x-2">
                  <i className="fas fa-database text-green-500 text-xs"></i>
                  <span className="text-green-600 text-xs font-medium">
                    {cachedInfo[opt.type].count} records cached - {new Date(cachedInfo[opt.type].savedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        {exportData && exportData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-8" style={{ animation: 'fadeSlideUp 0.4s ease-out' }}>
            <h3 className="font-bold text-gray-800 mb-4">
              <i className="fas fa-download mr-2 text-indigo-600"></i>
              Export {exportData.length} records
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={handleDownloadCSV}
                className="flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
                data-testid="download-csv-btn"
              >
                <i className="fas fa-file-csv text-xl"></i>
                <span>Download CSV</span>
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
                data-testid="print-report-btn"
              >
                <i className="fas fa-print text-xl"></i>
                <span>Print Report</span>
              </button>
            </div>
          </div>
        )}

        {/* Preview Table */}
        {exportData && exportData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600">
              <h3 className="text-white font-bold">Preview ({exportData.length} records)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-3 px-4 text-gray-500 text-xs uppercase font-semibold">Box #</th>
                    <th className="text-left py-3 px-4 text-gray-500 text-xs uppercase font-semibold">Recipient</th>
                    <th className="text-left py-3 px-4 text-gray-500 text-xs uppercase font-semibold">Carrier</th>
                    <th className="text-left py-3 px-4 text-gray-500 text-xs uppercase font-semibold">Status</th>
                    <th className="text-left py-3 px-4 text-gray-500 text-xs uppercase font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {exportData.slice(0, 20).map((pkg, i) => (
                    <tr key={pkg.id || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-gray-800">#{pkg.box_number}</td>
                      <td className="py-3 px-4 text-gray-700">{pkg.recipient_name || '-'}</td>
                      <td className="py-3 px-4 text-gray-500">{pkg.carrier || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          pkg.is_picked_up ? 'bg-blue-100 text-blue-700' : pkg.archived ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'
                        }`}>
                          {pkg.is_picked_up ? 'Picked Up' : pkg.archived ? 'Archived' : 'Available'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-sm">{pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {exportData.length > 20 && (
                <div className="px-6 py-3 bg-gray-50 text-center text-gray-400 text-sm">
                  Showing 20 of {exportData.length} records. Download CSV for full data.
                </div>
              )}
            </div>
          </div>
        )}

        {exportData && exportData.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <i className="fas fa-inbox text-gray-300 text-5xl mb-4"></i>
            <p className="text-gray-400 text-lg">No records found for this category</p>
          </div>
        )}

        {loading && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <i className="fas fa-spinner fa-spin text-indigo-500 text-3xl mb-4"></i>
            <p className="text-gray-500">Loading data...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataExport;
